import { Link } from 'react-router-dom';
import { SIGNUP_URL } from '../config';
import { CheckGreen, ChevronRight } from '../components/icons';

const industryCards = [
  { name: 'Retail shops', desc: 'General merchandise, multi-product retail with regular restocking and varied payment types.' },
  { name: 'Convenience stores', desc: 'Fast-moving consumer goods, high transaction volume, quick service counters.' },
  { name: 'Pharmacies', desc: 'Product tracking by SKU, stock control, clear financial records.' },
  { name: 'Fashion & apparel', desc: 'Variants by size and colour, seasonal inventory, customer receipts.' },
  { name: 'Electronics & accessories', desc: 'High-value items and careful stock control.' },
  { name: 'Beauty & cosmetics', desc: 'Product catalogue management, fast selling, payment flexibility.' },
  { name: 'Wholesale', desc: 'Bulk inventory, multi-unit sales, financial records for large transactions.' },
  { name: 'Supermarkets', desc: 'Multi-cashier, high volume, daily cash reconciliation, consolidated reporting.' },
];

const sizeSolutions = [
  {
    id: 'small',
    label: 'Small business',
    headline: 'Get control without building a finance department.',
    desc: 'cevop gives small businesses one system to sell, manage products and stock when needed, buy from suppliers, and understand money — without complexity.',
    benefits: [
      'Business workspace + POS on one set of books',
      'Know cash position and what is owed',
      'Stock updates from sales when you track it',
      'Know what you sold, what it cost, and what you made',
    ],
  },
  {
    id: 'growing',
    label: 'Growing business',
    headline: 'Keep operations connected as you scale.',
    desc: 'Multiple cashiers, a growing catalogue, and role-based access — with per-cashier reporting and accurate books.',
    benefits: [
      'Multiple cashiers with individual accountability',
      'Role-based access — cashiers, managers, owners',
      'Per-cashier and aggregate reports',
      'Cash control across multiple POS registers',
    ],
  },
  {
    id: 'multi',
    label: 'Multi-location',
    headline: 'See every location without chasing every location.',
    desc: 'Each location has its own operations. You get a view across all of them on Growth and Enterprise.',
    benefits: [
      'Consolidated sales across locations',
      'Per-location POS control',
      'Stock per location when you track it',
      'Central staff and access management',
    ],
  },
];

export function SolutionsPage() {
  return (
    <>
      <section className="px-6 pt-28 pb-16 lg:px-10 lg:pt-36 lg:pb-20">
        <div className="mx-auto max-w-[1280px] text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand/15 bg-brand-light px-3 py-1.5 text-[0.72rem] font-bold tracking-widest text-brand uppercase">
            Solutions
          </div>
          <h1 className="font-display mx-auto mb-5 max-w-[700px] text-[2.75rem] leading-[1.06] font-extrabold text-ink lg:text-[3.625rem]">
            Built for businesses that sell.
          </h1>
          <p className="mx-auto max-w-[520px] text-[1.0625rem] leading-[1.7] text-ink-3">
            Retail, convenience, beauty, pharmacy, fashion, electronics. If you sell products and need stock and money
            connected — cevop is for you. Stock tracking is there when you need it, not forced on every business.
          </p>
        </div>
      </section>

      <section className="border-t border-line px-6 py-16 lg:px-10">
        <div className="mx-auto max-w-[1280px]">
          <div className="mb-10 text-center">
            <h2 className="font-display mb-4 text-[2rem] font-extrabold text-ink">Not just one industry. All of them.</h2>
            <p className="mx-auto max-w-[480px] text-[1rem] text-ink-3">
              cevop is not a restaurant platform. If you sell, manage stock and handle money — it works for you.
            </p>
          </div>
          <div className="mk-stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {industryCards.map((item) => (
              <div key={item.name} className="mk-lift rounded-2xl border border-line bg-panel p-5 shadow-sm">
                <div className="font-display mb-1.5 text-[0.9rem] font-bold text-ink">{item.name}</div>
                <div className="text-[0.78rem] leading-[1.6] text-ink-3">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-surface-2/60 px-6 py-20 lg:px-10 lg:py-28">
        <div className="mx-auto max-w-[1280px] space-y-16">
          {sizeSolutions.map((s) => (
            <div key={s.id} id={s.id} className="scroll-mt-24">
              <div className="mb-4 inline-flex items-center rounded-full border border-brand/15 bg-brand-light px-2.5 py-1 text-[0.68rem] font-bold tracking-widest text-brand uppercase">
                {s.label}
              </div>
              <h3 className="font-display mb-4 text-[1.75rem] leading-[1.1] font-extrabold text-ink lg:text-[2.25rem]">
                {s.headline}
              </h3>
              <p className="mb-6 max-w-[60ch] text-[0.95rem] leading-[1.7] text-ink-3">{s.desc}</p>
              <div className="mb-7 space-y-2.5">
                {s.benefits.map((b) => (
                  <div key={b} className="flex items-start gap-3">
                    <CheckGreen />
                    <span className="text-[0.85rem] text-ink-2">{b}</span>
                  </div>
                ))}
              </div>
              <a
                href={SIGNUP_URL}
                className="inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-2.5 text-[0.875rem] font-semibold text-brand-on hover:bg-brand-hover"
              >
                Start free <ChevronRight />
              </a>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-[640px] text-center">
          <h2 className="font-display mb-5 text-[2.25rem] font-extrabold text-ink">Which business is yours?</h2>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href={SIGNUP_URL}
              className="inline-flex items-center justify-center rounded-xl bg-brand px-8 py-3.5 font-semibold text-brand-on hover:bg-brand-hover"
            >
              Start free
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl border border-line-strong px-8 py-3.5 font-semibold text-ink hover:bg-surface-2"
            >
              Talk to us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
